<script setup lang="ts">
/**
 * A widget of an app sidebar (NxAppSidebar): a titled block of content that is
 * not navigation — a quota, a sync state, a mini calendar, scheduled sends. It
 * sits in the sidebar's scrolling area or in its `bottom` slot.
 *
 * The head carries the `title` (in the uppercase label style of NxNavSection),
 * an optional `icon` and small buttons in `#actions` (a refresh). The block is
 * a `region` named by its title.
 *
 * `collapsible` turns the title into a disclosure button (`aria-expanded`,
 * `aria-controls`) that folds the body away; the open state is `v-model:open`
 * (open by default) and, with `storageKey`, is remembered in the user's
 * settings (`shell.panes.<storageKey>.open`, the kit's settings contract,
 * I-2510). The body stays mounted while folded.
 *
 * Not NxNavSection (a `<nav>` of rows) and not NxSidebarSection (a block of a
 * record sidebar, with a bold heading and no folding).
 */
import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js';
import { computed, useId, watch, type Component } from 'vue';
import { useNxPaneState } from '../Settings/settingsSource';

const props = withDefaults(
  defineProps<{
    title: string;
    /** Small icon before the title. */
    icon?: Component;
    /** The title folds and unfolds the body. */
    collapsible?: boolean;
    /** Remember the open state in the user's settings (`shell.panes.<storageKey>`). */
    storageKey?: string;
  }>(),
  { icon: undefined, collapsible: false, storageKey: '' }
);

const open = defineModel<boolean>('open', { default: true });

defineSlots<{
  default?: () => unknown;
  /** Small buttons at the end of the head (NxIconButton, height `xs`/`s`). */
  actions?: () => unknown;
}>();

const titleId = `nx-sidebar-widget-title-${useId()}`;
const bodyId = `nx-sidebar-widget-body-${useId()}`;

const pane = useNxPaneState<{ open: boolean }>(() => (props.collapsible ? props.storageKey : ''));
const stored = computed<boolean | null>(() => {
  const value = pane.state.value.open;
  return typeof value === 'boolean' ? value : null;
});
// A stored state wins over the start value, so the parent's ref learns it —
// also when it arrives after the mount (the settings answered).
const adopt = (value: boolean | null) => {
  if (value !== null && value !== open.value) open.value = value;
};
adopt(stored.value);
watch(stored, adopt);
watch(open, (value) => {
  if (value !== stored.value) pane.patch({ open: value });
});

function toggle() {
  open.value = !open.value;
}
</script>

<template>
  <section
    class="nx-sidebar-widget"
    :class="{ 'is-collapsible': props.collapsible, 'is-open': !props.collapsible || open }"
    :aria-labelledby="titleId"
    data-testid="nx-sidebar-widget"
  >
    <div class="nx-sidebar-widget__head">
      <button
        v-if="props.collapsible"
        type="button"
        class="nx-sidebar-widget__toggle"
        :aria-expanded="open"
        :aria-controls="bodyId"
        data-testid="nx-sidebar-widget-toggle"
        @click="toggle"
      >
        <ChevronRight class="nx-sidebar-widget__chevron nxt-icon-sm" aria-hidden="true" />
        <component :is="props.icon" v-if="props.icon" class="nxt-icon-sm" aria-hidden="true" />
        <span :id="titleId" class="nx-sidebar-widget__title">{{ props.title }}</span>
      </button>
      <div v-else class="nx-sidebar-widget__heading">
        <component :is="props.icon" v-if="props.icon" class="nxt-icon-sm" aria-hidden="true" />
        <span :id="titleId" class="nx-sidebar-widget__title">{{ props.title }}</span>
      </div>
      <div v-if="$slots.actions" class="nx-sidebar-widget__actions">
        <slot name="actions" />
      </div>
    </div>
    <div
      v-show="!props.collapsible || open"
      :id="bodyId"
      class="nx-sidebar-widget__body"
      data-testid="nx-sidebar-widget-body"
    >
      <slot />
    </div>
  </section>
</template>

<style scoped>
.nx-sidebar-widget {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

/* Same rhythm as NxNavSection's header, so widgets and nav sections line up. */
.nx-sidebar-widget__head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-height: 1.125em;
  margin-top: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}

.nx-sidebar-widget__heading,
.nx-sidebar-widget__toggle {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  color: var(--nxt-fg-subtle);
}

.nx-sidebar-widget__toggle {
  margin: calc(var(--nxt-space-2xs) * -1);
  padding: var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background var(--nxt-motion-fast);
}

.nx-sidebar-widget__toggle:hover {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-fg);
}

.nx-sidebar-widget__toggle:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-sidebar-widget__chevron {
  flex: 0 0 auto;
  transition: transform var(--nxt-motion-fast);
}

.nx-sidebar-widget.is-open .nx-sidebar-widget__chevron {
  transform: rotate(90deg);
}

.nx-sidebar-widget__title {
  min-width: 0;
  overflow: hidden;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.1em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.nx-sidebar-widget__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
  margin-left: auto;
  color: var(--nxt-fg-subtle);
}

.nx-sidebar-widget__body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
  padding-inline: var(--nxt-space-xs);
}

@media (prefers-reduced-motion: reduce) {
  .nx-sidebar-widget__chevron {
    transition: none;
  }
}
</style>
